// Promo-coupon config helpers, shared by /pricing (which renders the discount)
// and the checkout controllers (which apply it). Both read the same
// `stripeCoupon` map from node-config, so they can't disagree about whether a
// promotion is still being offered.
//
// The map is keyed by the literal Stripe promotion code — `couponDataFromPromotionCode`
// looks the key up with `stripe.promotionCodes.list({ code })` — and each entry
// carries the copy and display rules for that promo:
//
//   pctDiscount  percent off, used to render the discounted price. MUST match the
//                Stripe coupon's `percent_off`, or the page advertises a discount
//                the customer isn't charged (SCI-2840).
//   bannerCopy   the text of the green banner on /pricing.
//   alwaysShow   auto-apply to every visitor, with no `?coupon=` param.
//   annualOnly   open the pricing toggle on yearly.
//   skipTrial    send the buyer to paid checkout instead of the free trial.
//   endsAt       ISO timestamp the promo stops being offered (see below).

// A promo's `endsAt` is the single source of truth for when it stops being
// offered. Every promo in this repo's history outlived its end date in config —
// BFCM40 by about 18 months — which left /pricing rendering a discount Stripe
// had stopped honoring, charging customers full price (SCI-2840). Expiring on
// the data means the offer ends on time whether or not anyone remembers to ship
// the removal commit.
//
// An entry with no `endsAt` never expires on its own; an unparseable one is
// treated as expired, because failing to show a live promo is a marketing miss
// while showing a dead one is a billing complaint.
const isCouponLive = (couponConfig, now = Date.now()) => {
  if (!couponConfig) {
    return false
  }
  if (!couponConfig.endsAt) {
    return true
  }
  const endsAt = Date.parse(couponConfig.endsAt)
  if (Number.isNaN(endsAt)) {
    return false
  }
  return now < endsAt
}

// Resolve a specific promo code (e.g. from `?coupon=`) to its config, or null if
// it isn't configured or has ended.
// Codes are upper-cased before lookup, so config keys must be upper-case too.
// /pricing already upper-cases its `?coupon=` param, but the checkout routes and
// the OAuth hand-off pass theirs through raw — without this, a lower-case code
// could miss the config (no skipTrial, no endsAt check) while still resolving in
// Stripe, applying a discount the page never agreed to.
const getLiveCoupon = (coupons, code, now = Date.now()) => {
  if (!code) {
    return null
  }
  const couponConfig = coupons?.[String(code).toUpperCase()]
  return isCouponLive(couponConfig, now) ? couponConfig : null
}

// The promo to apply with no opt-in from the visitor, as `{ code, config }`, or
// null when none is running. Only the first live `alwaysShow` entry is used —
// two auto-applied promos at once would race for the same banner.
const getAutoApplyCoupon = (coupons, now = Date.now()) => {
  const entry = Object.entries(coupons || {}).find(([, couponConfig]) => {
    return !!couponConfig?.alwaysShow && isCouponLive(couponConfig, now)
  })
  return entry ? { code: entry[0], config: entry[1] } : null
}

module.exports = {
  isCouponLive,
  getLiveCoupon,
  getAutoApplyCoupon
}
